import { useEffect, useRef } from 'react'
import { useMutation as useRQMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useSelf, useOthers, useStorage } from '@/lib/liveblocks'
import { useLayoutStore } from '@/stores/layoutStore'

/**
 * Replaces `useAutoSave` when the editor is wrapped in a
 * `<LayoutRoom>`. Zustand's `mutationQueue` is unused in the
 * multiplayer path — Liveblocks Storage is authoritative, and
 * every peer has the same view, so we push a full snapshot to
 * DynamoDB instead of a batched diff.
 *
 * **Leader election:** only one peer per room should save. We
 * pick the peer with the lowest `connectionId` among everyone
 * currently connected. Liveblocks assigns monotonically
 * increasing connectionIds on join, so the "lowest" is stable
 * across everybody's view: each peer independently computes the
 * same leader. On disconnect, the next-lowest takes over
 * automatically without any coordination message.
 *
 * **Trigger:** debounced on `meta.version` changes (bumped by
 * every `useMultiplayerLayoutSync` dispatcher mutation). 2-second
 * debounce matches the single-player behaviour.
 *
 * **Payload:** the current Zustand snapshot — populated by the
 * Storage→Zustand mirror in `useMultiplayerLayoutSync`, so by
 * the time the debounce fires, Zustand has the freshest state
 * every peer agrees on.
 */
export function useMultiplayerAutoSave(layoutId: string): {
  isLeader: boolean
  isSaving: boolean
} {
  const self = useSelf()
  const others = useOthers()

  // Lowest connectionId wins. `self` can be null briefly on
  // reconnect; treat that as "not leader" for now.
  const myId = self?.connectionId
  const lowestOther = others.reduce<number | null>(
    (min, o) => (min === null || o.connectionId < min ? o.connectionId : min),
    null,
  )
  const isLeader =
    myId !== undefined && (lowestOther === null || myId < lowestOther)

  // Watch `meta.version` as a single scalar — Liveblocks bumps it
  // on every mutation, so one subscription covers every write.
  const version = useStorage(root => root.meta?.version) as number | null

  const saveMutation = useRQMutation({
    mutationFn: async (payload: {
      id: string
      version: number
      devices: unknown[]
      connections: unknown[]
      zones: unknown[]
    }) =>
      apiClient.put<{ version: number }>(`/api/v1/layouts/${payload.id}`, {
        version: payload.version,
        devices: payload.devices,
        connections: payload.connections,
        zones: payload.zones,
      }),
    onSuccess: data => {
      // Server bumps version N → N+1 on successful write and echoes
      // the new value. Propagate that to Zustand so the next save
      // sends the fresh version instead of the stale one — without
      // this, every subsequent save would race against DynamoDB's
      // conditional-expression check and 409 indefinitely.
      if (data?.version !== undefined) {
        useLayoutStore.getState().setLayoutVersion(data.version)
      }
    },
    onError: (err: unknown) => {
      // On 409 the server tells us what version it actually has via
      // `currentVersion` in the error body. Adopt that so the NEXT
      // retry — triggered by the next meta.version bump — uses the
      // right baseline. Without this, the client keeps POSTing with
      // its stale version and stays in a 409 loop.
      const body = (err as { body?: { currentVersion?: number } } | null)?.body
      const current = body?.currentVersion
      if (typeof current === 'number') {
        useLayoutStore.getState().setLayoutVersion(current)
      }
    },
  })

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedVersion = useRef<number | null>(null)

  useEffect(() => {
    if (!isLeader) return
    if (version === null) return
    if (version === lastSavedVersion.current) return

    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      // Read the latest Zustand state at flush time — not a
      // stale snapshot from when the effect was scheduled. The
      // Storage subscription keeps Zustand caught up on every
      // peer's writes, so this is always the agreed-upon state.
      const state = useLayoutStore.getState()
      // Guard against racing with our own previous flight — if
      // `saveMutation` is pending, skip this tick; the next
      // version bump will queue another attempt.
      if (saveMutation.isPending) return
      saveMutation.mutate(
        {
          id: layoutId,
          version: state.layoutVersion,
          devices: state.devices,
          connections: state.connections,
          zones: state.zones,
        },
        {
          onSuccess: () => {
            lastSavedVersion.current = version
          },
        },
      )
    }, 2000)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [isLeader, version, layoutId, saveMutation])

  return {
    isLeader,
    isSaving: saveMutation.isPending,
  }
}
